import type { TypedDocumentNode } from '@apollo/client'
import type { ScormApiBridge } from '~/utils/scorm'
import { useApolloClient } from '@apollo/client/react'
import { useCallback, useRef, useState } from 'react'
import { GQL_MY_SCORM_RESUME } from '~/gql/scorm/scorm'
import { useScoNavigation } from './use-sco-navigation'

type ResultOf<D> = D extends TypedDocumentNode<infer R, never> ? R : never
/** L'état de reprise, tel que le lancement et la relecture le rendent (même sélection). */
export type ResumeState = NonNullable<ResultOf<typeof GQL_MY_SCORM_RESUME>['myScormResume']>

/**
 * Le lecteur d'un module à plusieurs SCO (#160) : quel chapitre jouer, depuis
 * quel état, et comment en changer.
 *
 * Changer de chapitre se fait dans cet ordre : le chapitre en cours rend la main
 * (Terminate) et ses sauvegardes partent, PUIS la reprise est relue — sa
 * dernière sauvegarde en fait partie — et alors seulement le nouveau chapitre
 * s'ouvre. La reprise relue est figée dans un état : surveillée dans le cache
 * Apollo, chaque sauvegarde la ferait changer, et le bridge du chapitre en
 * cours serait remonté en pleine lecture.
 */
export function useChapterPlayer(
  ids: { sessionId?: string, moduleId?: string },
  opened: ResumeState | null | undefined,
) {
  const client = useApolloClient()
  const bridgeRef = useRef<ScormApiBridge | null>(null)
  const [reread, setReread] = useState<ResumeState | null>(null)
  const [switching, setSwitching] = useState(false)
  const resume = reread ?? opened
  const chapters = useScoNavigation(ids.moduleId, resume)
  const { select } = chapters

  const switchTo = useCallback(async (identifier: string) => {
    const bridge = bridgeRef.current
    if (bridge?.isInitialized()) bridge.Terminate()
    setSwitching(true)
    try {
      await bridge?.flushCommits()
      if (ids.sessionId && ids.moduleId) {
        const { data } = await client.query({
          query: GQL_MY_SCORM_RESUME,
          variables: { sessionId: ids.sessionId, moduleId: ids.moduleId },
          fetchPolicy: 'network-only',
        })
        if (data?.myScormResume) setReread(data.myScormResume)
      }
    } catch {
      // Reprise non relue : on rouvre depuis l'état connu, plutôt que de bloquer l'apprenant.
    } finally {
      select(identifier)
      setSwitching(false)
    }
  }, [client, select, ids.sessionId, ids.moduleId])

  return {
    ...chapters,
    resume,
    switching,
    bridgeRef,
    switchTo,
    /** Le chapitre joué, pour un module à plusieurs SCO ; aucun sinon. */
    playing: chapters.multi ? chapters.active : undefined,
  }
}
